import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  Bot,
  ClipboardList,
  CreditCard,
  LayoutDashboard,
  MessagesSquare,
  Package2,
  PanelLeftClose,
  PanelLeftOpen,
  PlugZap,
  Sparkles,
  UsersRound,
} from 'lucide-react';
import { fetchApi } from '../api';
import { getSocket } from '../socket';
import { formatDate, formatNumber, loadUserSettings } from '../utils/userSettings';
import '../styles/dashboard.css';
import '../styles/settings.css';
import '../styles/sidebar.css';

/**
 * The sidebar used to make two claims it could not support:
 *
 *  - "Автопилот активен · 87% диалогов" — 87 was a literal in this file, the same
 *    for every account on every day.
 *  - "Осталось 3 000 ИИ-сообщений" — the remainder came from raw subscription
 *    columns with `?? 3000` fallbacks, so an account whose paid term had expired
 *    (and whose agent was therefore not answering at all) was cheerfully shown an
 *    unspent quota, on a plan name it no longer had.
 *
 * Both now come from `GET /api/stats/sidebar`: the real share of dialogs the agent
 * answered in the last week, and `EntitlementService`'s view of the plan — which
 * reports a lapsed paid term as the free plan and rolls the free window forward.
 */

interface SidebarProps {
  activeView: string;
  setActiveView: (view: string) => void;
  autopilot?: boolean;
  setAutopilot?: (v: boolean) => void;
}

interface AutopilotStats {
  windowDays: number;
  activeDialogs: number;
  aiDialogs: number;
  share: number | null;
}

interface SidebarStats {
  plan: string;
  effectivePlan: string;
  planName: string;
  state: 'active' | 'expiring' | 'expired' | 'canceled';
  paid: boolean;
  downgraded: boolean;
  currentPeriodEnd: string;
  daysLeft: number;
  aiMessagesUsed: number;
  aiMessagesLimit: number;
  aiMessagesLeft: number;
  unlimited: boolean;
  autopilot: AutopilotStats;
}

export default function Sidebar({ activeView, setActiveView, autopilot, setAutopilot }: SidebarProps) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [openDealsCount, setOpenDealsCount] = useState(0);
  const [stats, setStats] = useState<SidebarStats | null>(null);
  const [statsFailed, setStatsFailed] = useState(false);

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('clientix_sidebar_collapsed') === 'true');
  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      localStorage.setItem('clientix_sidebar_collapsed', String(next));
      return next;
    });
  };

  const fetchStats = useCallback(() => {
    fetchApi('/stats/sidebar')
      .then((data) => {
        setStats(data);
        setStatsFailed(false);
      })
      .catch(() => {
        // Never fall back to invented numbers: show nothing instead.
        setStats(null);
        setStatsFailed(true);
      });
  }, []);

  const fetchUnread = useCallback(() => {
    fetchApi('/conversations')
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        setUnreadCount(list.reduce((acc: number, c: any) => acc + (c.unread || 0), 0));
      })
      .catch(() => setUnreadCount(0));
  }, []);

  const fetchDealsCount = useCallback(() => {
    fetchApi('/deals')
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        setOpenDealsCount(list.filter((d: any) => d.status === 'open' || !d.status).length);
      })
      .catch(() => setOpenDealsCount(0));
  }, []);

  useEffect(() => {
    // The sidebar is mounted on every screen, so it is the earliest point at
    // which the account's theme and language can be applied.
    loadUserSettings().catch(() => {});
  }, []);

  useEffect(() => {
    const socket = getSocket();
    const handler = () => {
      fetchUnread();
      fetchDealsCount();
      fetchStats();
    };

    if (socket) {
      socket.on('message:new', handler);
      socket.on('deal:new', handler);
      socket.on('deal:update', handler);
      socket.on('subscription:updated', handler);
    }

    window.addEventListener('chat:read', handler);

    return () => {
      if (socket) {
        socket.off('message:new', handler);
        socket.off('deal:new', handler);
        socket.off('deal:update', handler);
        socket.off('subscription:updated', handler);
      }
      window.removeEventListener('chat:read', handler);
    };
  }, [fetchUnread, fetchDealsCount, fetchStats]);

  useEffect(() => {
    fetchUnread();
    fetchDealsCount();
    fetchStats();
  }, [activeView, fetchUnread, fetchDealsCount, fetchStats]);

  const navItems = [
    { id: 'dashboard', label: 'Дашборд', icon: <LayoutDashboard /> },
    { id: 'chats', label: 'Чаты', icon: <MessagesSquare />, badge: unreadCount },
    { id: 'requests', label: 'Заявки', icon: <ClipboardList />, badge: openDealsCount },
    { id: 'warehouse', label: 'Склад', icon: <Package2 /> },
  ];

  const automationItems = [
    { id: 'ai', label: 'ИИ-агент', icon: <Sparkles /> },
    { id: 'integrations', label: 'Источники лидов', icon: <PlugZap /> },
  ];

  const managementItems = [
    { id: 'team', label: 'Команда', icon: <UsersRound /> },
    { id: 'billing', label: 'Тариф и оплата', icon: <CreditCard /> },
  ];

  /** Only ever states what is known to be true. */
  const autopilotText = () => {
    if (!autopilot) return 'Агент на паузе — отвечают менеджеры';
    const share = stats?.autopilot?.share;
    if (share === null || share === undefined) return 'Автопилот включён';
    const { aiDialogs, activeDialogs, windowDays } = stats!.autopilot;
    return `Автопилот включён · отвечает в ${share}% диалогов (${aiDialogs} из ${activeDialogs} за ${windowDays} дн.)`;
  };

  const quotaPercent = () => {
    if (!stats || stats.unlimited || stats.aiMessagesLimit <= 0) return 0;
    return Math.min(100, Math.round((stats.aiMessagesUsed / stats.aiMessagesLimit) * 100));
  };

  const renderPlanCard = () => {
    if (statsFailed && !stats) {
      return (
        <div className="plan-card">
          <b>Тариф</b>
          <br />
          Не удалось загрузить данные тарифа
          <button onClick={() => setActiveView('billing')}>Открыть тарифы</button>
        </div>
      );
    }
    if (!stats) {
      return (
        <div className="plan-card" aria-busy="true">
          <b>Тариф</b>
          <br />
          Загрузка…
        </div>
      );
    }

    const expired = stats.state === 'expired' || stats.downgraded;
    const endLabel = formatDate(stats.currentPeriodEnd, { day: 'numeric', month: 'long' });

    return (
      <div className="plan-card">
        <b>Тариф {stats.planName}</b>
        {expired ? (
          <>
            <span className="plan-card-state danger">оплаченный период закончился</span>
            <br />
            Действуют лимиты тарифа «{stats.planName}» с {endLabel}
          </>
        ) : (
          <>
            {stats.state === 'expiring' && (
              <span className="plan-card-state warn">
                осталось {stats.daysLeft} дн.
              </span>
            )}
            {stats.state === 'canceled' && <span className="plan-card-state warn">продление отключено</span>}
            <br />
            {stats.paid ? 'Оплачен до ' : 'Период до '}
            {endLabel}
          </>
        )}
        <br />
        {stats.unlimited ? (
          'ИИ-ответы без ограничений'
        ) : (
          <>
            Осталось {formatNumber(stats.aiMessagesLeft)} из {formatNumber(stats.aiMessagesLimit)} ИИ-ответов
            <span className="plan-quota-bar" aria-hidden="true">
              <i style={{ width: `${quotaPercent()}%` }} />
            </span>
          </>
        )}
        <button onClick={() => setActiveView('billing')}>
          {expired ? 'Продлить тариф' : 'Улучшить план'}
        </button>
      </div>
    );
  };

  const renderNavGroup = (
    label: string,
    items: Array<{ id: string; label: string; icon: ReactNode; badge?: number }>,
  ) => (
    <div className="nav-group">
      <div className="nav-label">{label}</div>
      {items.map((item) => (
        <button
          key={item.id}
          className={`nav-item ${activeView === item.id ? 'active' : ''}`}
          aria-current={activeView === item.id ? 'page' : undefined}
          aria-label={collapsed ? item.label : undefined}
          data-tooltip={item.label}
          onClick={() => setActiveView(item.id)}
        >
          <span className="nav-icon">{item.icon}</span>
          <span className="nav-text">{item.label}</span>
          {typeof item.badge === 'number' && item.badge > 0 && (
            <span className="nav-badge">{item.badge > 99 ? '99+' : item.badge}</span>
          )}
        </button>
      ))}
    </div>
  );

  return (
    <aside className={`sidebar sidebar-redesign ${collapsed ? 'is-collapsed' : ''}`} id="sidebar">
      <div className="sidebar-head">
        <button className="logo" onClick={() => setActiveView('dashboard')} aria-label="Открыть дашборд">
          <div className="logo-mark">
            <img src="/logo.svg" alt="" />
          </div>
          <div className="logo-text">
            Clientix<span>центр продаж для бизнеса</span>
          </div>
        </button>
        <button
          className="sidebar-collapse"
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
        >
          {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
        </button>
      </div>

      <nav aria-label="Основная навигация">
        {renderNavGroup('Работа', navItems)}
        {renderNavGroup('Автоматизация', automationItems)}
        {renderNavGroup('Управление', managementItems)}
      </nav>

      <div className="sb-footer">
        <div className="ap-card">
          <div className="ap-row">
            <span className="ap-name">
              <span className="agent-icon"><Bot /></span>
              <span className="agent-name">
                ИИ-агент Clientix<small>{autopilot ? 'Работает' : 'На паузе'}</small>
              </span>
            </span>
            <label className="switch" aria-label="Автопилот ИИ-агента">
              <input
                type="checkbox"
                className="ap-switch"
                checked={!!autopilot}
                onChange={(e) => setAutopilot && setAutopilot(e.target.checked)}
              />
              <span className="slider"></span>
            </label>
          </div>
          <div className="ap-state">
            <span className={`ap-dot ${autopilot ? 'on' : ''}`}></span>
            <span className="ap-state-text">{autopilotText()}</span>
          </div>
        </div>
        {renderPlanCard()}
      </div>

      <div className="sb-collapsed-tools">
        <button
          className={`collapsed-tool ${autopilot ? 'is-on' : ''}`}
          onClick={() => setAutopilot?.(!autopilot)}
          aria-label={autopilot ? 'Поставить ИИ-агента на паузу' : 'Включить ИИ-агента'}
          data-tooltip="ИИ-агент Clientix"
        >
          <Bot />
          <span className="status-dot" />
        </button>
        <button
          className="collapsed-tool"
          onClick={() => setActiveView('billing')}
          aria-label="Открыть тариф"
          data-tooltip="Тариф и оплата"
        >
          <CreditCard />
        </button>
      </div>
    </aside>
  );
}
